import React from 'react';
import HabboImage from '../HabboImage';
import { frameUrl, frameDoc, frameArticle } from './frameLinks';

const PIXEL_IMAGE = {
    width: 'auto',
    height: 'auto',
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'none',
    flexShrink: 0,
    imageRendering: 'pixelated',
};

const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Inside the frame an article swaps the frame's own contents; anything external
// has to break out to _top so the whole AskFrank page re-frames it, which is
// what keeps a guest from ever leaving the desk.
const mentionTarget = (mention) => (
    mention.articleSlug
        ? frameArticle({ slug: mention.articleSlug, habboId: mention.habboId })
        : frameUrl(mention.url)
);

function FactRow({ fact }) {
    const value = fact.value || '';

    return (
        <div className="flex flex-col gap-[2px] border-b border-gray-200 py-2 last:border-b-0 sm:flex-row sm:gap-4">
            <span className="w-full shrink-0 text-[11px] uppercase tracking-[1px] text-gray-500 sm:w-[150px]">
                {fact.label}
            </span>

            {fact.url ? (
                <a
                    href={fact.url}
                    target={fact.url.startsWith('/') ? '_top' : '_blank'}
                    rel="noreferrer noopener"
                    className="min-w-0 flex-1 text-[14px] text-maroon underline hover:text-gold"
                >
                    {value || fact.url}
                </a>
            ) : (
                <span className="min-w-0 flex-1 text-[14px] text-gray-800">{value}</span>
            )}
        </div>
    );
}

function DocEntity({ entity }) {
    return (
        <>
            <div className="mb-3 flex flex-wrap items-center gap-2 border border-gray-300 bg-offwhite px-3 py-2">
                <span className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-maroon">
                    Frank&apos;s card
                </span>
                <span className="bg-maroon px-2 py-[1px] text-[10px] font-bold uppercase text-white">
                    {entity.subtypeLabel || entity.kindLabel}
                </span>
                {entity.protected && (
                    <span className="text-[11px] uppercase tracking-[1px] text-gray-400" title="Only what the desk has approved is shown here">
                        Curated only
                    </span>
                )}
                {entity.linkUrl && (
                    <a
                        href={entity.linkUrl.startsWith('/')
                            ? frameDoc(entity.linkUrl)
                            : frameUrl(entity.linkUrl)}
                        target="_top"
                        rel="noreferrer noopener"
                        className="ml-auto border border-gray-300 bg-white px-3 py-1 text-[12px] uppercase text-maroon hover:bg-gray-100"
                    >
                        {entity.linkLabel || (entity.linkUrl.startsWith('/') ? 'See Frankly Profile' : 'Open the source')}
                    </a>
                )}
            </div>

            <div className="border border-gray-300 bg-white">
                <div className="flex flex-col gap-4 border-b border-gray-200 p-5 sm:flex-row sm:items-start">
                    {entity.imageUrl ? (
                        <div className="flex shrink-0 items-start justify-center bg-offwhite p-2 sm:justify-start">
                            <img
                                src={entity.imageUrl}
                                alt=""
                                aria-hidden="true"
                                draggable={false}
                                style={entity.kind === 'furni' ? PIXEL_IMAGE : { maxWidth: '160px', height: 'auto' }}
                            />
                        </div>
                    ) : entity.avatar ? (
                        <div className="flex shrink-0 items-start justify-center bg-offwhite p-2 sm:justify-start">
                            <HabboImage
                                habbo={entity.avatar.habbo}
                                hotel={entity.avatar.hotel}
                                figure={entity.avatar.figure}
                                size="b"
                                direction={2}
                                head_direction={3}
                                alt=""
                                aria-hidden="true"
                                draggable={false}
                                style={PIXEL_IMAGE}
                            />
                        </div>
                    ) : null}

                    <div className="min-w-0 flex-1">
                        <h1 className="font-['Jersey_10'] text-[32px] uppercase leading-none tracking-[1px] text-maroon">
                            {entity.name}
                        </h1>

                        {entity.headline && (
                            <p className="mt-1 text-[13px] uppercase tracking-[1px] text-gray-500">
                                {entity.headline}
                            </p>
                        )}

                        {entity.summary && (
                            <p className="mt-3 text-[15px] leading-relaxed text-gray-800">{entity.summary}</p>
                        )}

                        {entity.member?.bio && (
                            <p className="mt-3 border-l-4 border-gold bg-offwhite p-3 text-[14px] italic leading-relaxed text-gray-700">
                                &ldquo;{entity.member.bio}&rdquo;
                            </p>
                        )}
                    </div>
                </div>

                {entity.body && (
                    <div className="border-b border-gray-200 p-5">
                        <p className="whitespace-pre-line text-[15px] leading-relaxed text-gray-800">{entity.body}</p>
                    </div>
                )}

                {entity.facts.length > 0 && (
                    <div className="border-b border-gray-200 p-5">
                        <p className="mb-2 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                            What Frank has on file
                        </p>
                        <div className="flex flex-col">
                            {entity.facts.map((fact, index) => (
                                <FactRow key={`${fact.label}-${index}`} fact={fact} />
                            ))}
                        </div>
                    </div>
                )}

                {entity.media.length > 0 && (
                    <div className="border-b border-gray-200 p-5">
                        <p className="mb-3 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                            From the scrapbook
                        </p>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {entity.media.map((item, index) => (
                                <figure key={`${item.imageUrl}-${index}`} className="border border-gray-200 bg-offwhite p-2">
                                    <img src={item.imageUrl} alt={item.caption || ''} className="block h-auto w-full" />
                                    {(item.caption || item.credit) && (
                                        <figcaption className="mt-2 text-[12px] leading-snug text-gray-600">
                                            {item.caption}
                                            {item.credit && <span className="block text-[10px] uppercase tracking-[1px] text-gray-400">{item.credit}</span>}
                                        </figcaption>
                                    )}
                                </figure>
                            ))}
                        </div>
                    </div>
                )}

                {entity.habbo?.badges?.length > 0 && (
                    <div className="border-b border-gray-200 p-5">
                        <p className="mb-1 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                            Badges
                        </p>
                        <p className="mb-3 text-[12px] text-gray-500">
                            What Frank has indexed. It may not be complete, and some may no longer be held in game.
                        </p>
                        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
                            {entity.habbo.badges.map(badge => (
                                <div key={badge.code} className="flex flex-col items-center gap-1 rounded bg-gray-100 p-2">
                                    <span className="text-center font-['Jersey_10'] text-[14px] uppercase leading-tight">
                                        {badge.name || badge.code}
                                    </span>
                                    <img
                                        src={`${process.env.PUBLIC_URL}/img/badges/${badge.code}.png`}
                                        alt={badge.name || badge.code}
                                        title={badge.description || badge.name || badge.code}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {entity.habbo?.looks?.length > 0 && (
                    <div className="border-b border-gray-200 p-5">
                        <p className="mb-3 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                            Previous looks
                        </p>
                        <div className="grid grid-cols-4 gap-3 sm:grid-cols-5 md:grid-cols-7">
                            {entity.habbo.looks.map((look, index) => (
                                <div
                                    key={`${look.figureString}-${index}`}
                                    className="flex flex-col items-center gap-1 rounded bg-gray-100 p-2"
                                >
                                    <HabboImage
                                        figure={look.figureString}
                                        direction={4}
                                        head_direction={4}
                                        size="a"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    <span className="text-center font-['Jersey_10'] text-[13px] text-gray-600">
                                        {formatDate(look.added) || ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {entity.profiles?.length > 0 && (
                    <div className="border-b border-gray-200 p-5">
                        <p className="mb-3 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                            Profiles on other sites
                        </p>
                        <div className="flex flex-col gap-2">
                            {entity.profiles.map(profile => (
                                <a
                                    key={profile.url}
                                    href={frameUrl(profile.url)}
                                    target="_top"
                                    rel="noreferrer noopener"
                                    className="flex items-center gap-2 border border-gray-200 bg-offwhite p-2 hover:bg-gray-100"
                                >
                                    {profile.faviconUrl && (
                                        <img src={profile.faviconUrl} alt="" aria-hidden="true" width={16} height={16} className="shrink-0" />
                                    )}
                                    <span className="shrink-0 text-[11px] uppercase tracking-[1px] text-gray-500">
                                        {profile.sourceName}
                                    </span>
                                    <span className="min-w-0 flex-1 truncate text-[13px] text-maroon">{profile.url}</span>
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {entity.related?.length > 0 && (
                    <div className="border-b border-gray-200 p-5">
                        <p className="mb-3 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                            Its record elsewhere
                        </p>
                        <div className="flex flex-col gap-2">
                            {entity.related.map(record => (
                                <a
                                    key={record.pageId}
                                    href={mentionTarget(record)}
                                    target="_top"
                                    rel="noreferrer noopener"
                                    className="flex items-center gap-2 border border-gray-200 bg-offwhite p-2 hover:bg-gray-100"
                                >
                                    {record.faviconUrl && (
                                        <img src={record.faviconUrl} alt="" aria-hidden="true" width={16} height={16} className="shrink-0" />
                                    )}
                                    <span className="shrink-0 text-[11px] uppercase tracking-[1px] text-gray-500">
                                        {record.sourceName}
                                    </span>
                                    <span className="min-w-0 flex-1 truncate text-[13px] text-maroon">{record.title}</span>
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {entity.context?.length > 0 && (
                    <div className="border-b border-gray-200 p-5">
                        <p className="mb-3 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                            Related reading
                        </p>
                        <div className="flex flex-col gap-2">
                            {entity.context.map(item => (
                                <a
                                    key={item.pageId}
                                    href={mentionTarget(item)}
                                    target="_top"
                                    rel="noreferrer noopener"
                                    className="flex flex-wrap items-center gap-2 border border-gray-200 bg-offwhite p-2 hover:bg-gray-100"
                                >
                                    <span className="shrink-0 bg-gold px-2 py-[1px] text-[10px] uppercase tracking-[1px] text-black/70">
                                        {item.via}
                                    </span>
                                    <span className="min-w-0 flex-1 truncate text-[13px] text-maroon">{item.title}</span>
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {entity.posts?.length > 0 && (
                    <div className="border-b border-gray-200 p-5">
                        <p className="mb-3 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                            What Frankly members have said
                        </p>
                        <div className="flex flex-col gap-2">
                            {entity.posts.map(post => (
                                <div key={post.id} className="border border-gray-200 bg-offwhite p-3">
                                    <a
                                        href={post.url}
                                        target="_top"
                                        className="font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-maroon hover:underline"
                                    >
                                        {post.author}
                                    </a>
                                    <p className="mt-1 text-[13px] leading-snug text-gray-700">{post.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {entity.mentions.length > 0 && (
                    <div className="p-5">
                        <p className="mb-3 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                            Where the name comes up
                        </p>

                        <div className="flex flex-col gap-3">
                            {entity.mentions.map(mention => (
                                <div key={mention.pageId} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                                    <div className="mb-[2px] flex flex-wrap items-center gap-2">
                                        {mention.faviconUrl && (
                                            <img src={mention.faviconUrl} alt="" aria-hidden="true" width={16} height={16} className="shrink-0" />
                                        )}
                                        <span className="truncate text-[12px] text-green-800">{mention.sourceDomain}</span>
                                        {mention.publishedAt && (
                                            <span className="text-[11px] text-gray-500">{formatDate(mention.publishedAt)}</span>
                                        )}
                                    </div>

                                    <a
                                        href={mentionTarget(mention)}
                                        target="_top"
                                        className="font-['Jersey_10'] text-[19px] uppercase leading-tight tracking-[1px] text-maroon hover:underline"
                                    >
                                        {mention.title}
                                    </a>

                                    {mention.description && (
                                        <p className="mt-1 text-[13px] leading-snug text-gray-700">{mention.description}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default DocEntity;
